import { PrismaEnums } from '@airreading/schema/browser';
import { z } from 'zod';

export const { AssessmentFormat } = PrismaEnums;
export type AssessmentFormat = PrismaEnums.AssessmentFormat;
export const assessmentFormatSchema = z.nativeEnum(AssessmentFormat);

const studentYearSchema = z.nativeEnum(PrismaEnums.StudentYear);

export const assessmentContentSchema = z.object({
  id: z.string(),
  format: assessmentFormatSchema,
  name: z.string(),
  words: z.string().array(),
  readingPassage: z.string().nullable(),
  maxErrorsAllowed: z.number().int(),
  internalOrder: z.number().int(),
  requireStudentGrades: z.array(studentYearSchema).nullable(),
});

export type AssessmentContent = z.infer<typeof assessmentContentSchema>;

export const assessmentContentSelectionOverrideSchema = z.object({
  assessmentId: z.string(),
  assessmentContentId: z.string(),
  lessonId: z.string(),
});
export type AssessmentContentSelectionOverride = z.infer<typeof assessmentContentSelectionOverrideSchema>;

export const assessmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
});
export type Assessment = z.infer<typeof assessmentSchema>;

export const assessmentWithContentSchema = assessmentSchema.extend({
  contents: z.array(assessmentContentSchema),
});
export type AssessmentWithContent = z.infer<typeof assessmentWithContentSchema>;

export const assessmentResultSchema = z.object({
  id: z.string(),
  sessionFeedbackSessionId: z.string(),
  sessionFeedbackCoachId: z.string(),
  sessionFeedbackStudentId: z.string(),
  assessmentContentId: z.string(),
  assessmentId: z.string(),
  assessmentFinished: z.boolean().nullable(),
  incorrectWords: z.array(z.string()),
  totalSecondsSpent: z.number().int().nullable(),
  totalWordsRead: z.number().int().nullable(),
  incorrectWordsCount: z.number().int().nullable(),
  phonemicReadingResult: z.boolean().nullable(),
  comprehensionSkillResult: z.string().nullable(),
  fluencySkillResult: z.string().nullable(),
  comprehensionPassageResult: z.boolean().nullable(),
  canRetell: z.boolean().nullable(),
  redoReason: z.string().nullable(),
  incorrectlWordsReadFromPMList: z.number().int().nullable(),
  incorrectWordsReadFromDiagnosticPassage: z.array(z.string()),
  finishedEarly: z.boolean().nullable(),
});

export type AssessmentResult = z.infer<typeof assessmentResultSchema>;

export const assessmentResultWithContentAndAssessmentSchema = assessmentResultSchema.extend({
  content: assessmentContentSchema,
  assessment: assessmentSchema,
});

export type AssessmentResultWithContentAndAssessment = z.infer<typeof assessmentResultWithContentAndAssessmentSchema>;

export const createAssessmentContentOptionsSchema = z.object({
  name: z.string(),
  format: assessmentFormatSchema,
  words: z.string().array(),
  readingPassage: z.string().nullable(),
  maxErrorsAllowed: z.number().int().nullable(),
  internalOrder: z.number().int(),
  assessmentId: z.string(),
  requireStudentGrades: z.array(studentYearSchema).nullable().optional(),
});

export type CreateAssessmentContentOptions = z.infer<typeof createAssessmentContentOptionsSchema>;

export const updateAssessmentContentOptionsSchema = z.object({
  name: z.string(),
  format: assessmentFormatSchema,
  words: z.string().array(),
  readingPassage: z.string().nullable(),
  maxErrorsAllowed: z.number().int().nullable(),
  internalOrder: z.number().int(),
  requireStudentGrades: z.array(studentYearSchema).nullable().optional(),
});
export type UpdateAssessmentContentOptions = z.infer<typeof updateAssessmentContentOptionsSchema>;

export const getAssessmentsResultsSchema = z.array(assessmentWithContentSchema);

export type GetAssessmentsResults = z.infer<typeof getAssessmentsResultsSchema>;

export const getAssessmentContentsResultsSchema = z.array(assessmentContentSchema);
export type GetAssessmentContentsResults = z.infer<typeof getAssessmentContentsResultsSchema>;

export const createAssessmentOptionsSchema = z.object({
  name: z.string(),
  displayName: z.string().nullable(),
  contentIds: z.string().array(),
});

export type CreateAssessmentOptions = z.infer<typeof createAssessmentOptionsSchema>;

export const updateAssessmentOptionsSchema = z.object({
  name: z.string(),
  displayName: z.string().nullable().optional(),
  contentIds: z.string().array(),
});
export type UpdateAssessmentOptions = z.infer<typeof updateAssessmentOptionsSchema>;

export const updateAssessmentResultOptionsSchema = z.object({
  assessmentFinished: z.boolean().optional().nullable(),
  incorrectWords: z.string().array(),
  totalSecondsSpent: z.number().int().optional().nullable(),
  totalWordsRead: z.number().int().optional().nullable(),
  incorrectWordsCount: z.number().int().optional().nullable(),
  phonemicReadingResult: z.boolean().optional().nullable(),
  comprehensionSkillResult: z.string().optional().nullable(),
  fluencySkillResult: z.string().optional().nullable(),
  comprehensionPassageResult: z.boolean().optional().nullable(),
  canRetell: z.boolean().optional().nullable(),
  incorrectlWordsReadFromPMList: z.number().int().optional().nullable(),
  incorrectWordsReadFromDiagnosticPassage: z.string().array(),
  finishedEarly: z.boolean().optional().nullable(),
});

export type UpdateAssessmentResultOptions = z.infer<typeof updateAssessmentResultOptionsSchema>;

/**
 * Progress Monitor formats: the original (v1) measures and the timed in-app (v2) replacements.
 * `progress_monitor_text` has no v2 sibling — `fluency_check` is the timed replacement and is not a PM format.
 */
export const PROGRESS_MONITOR_FORMATS = [
  AssessmentFormat.progress_monitor_words,
  AssessmentFormat.progress_monitor_words_v2,
  AssessmentFormat.progress_monitor_sounds,
  AssessmentFormat.progress_monitor_sounds_v2,
  AssessmentFormat.progress_monitor_letter_names,
  AssessmentFormat.progress_monitor_letter_names_v2,
  AssessmentFormat.progress_monitor_text,
] as const satisfies readonly AssessmentFormat[];

export function isProgressMonitorFormat(format: AssessmentFormat): boolean {
  return (PROGRESS_MONITOR_FORMATS as readonly AssessmentFormat[]).includes(format);
}

/** PM and GLA measures that flag a teacher for a PM/GLA TQ review when left unfinished. */
export const PM_GLA_TQ_REVIEW_FORMATS = [
  AssessmentFormat.progress_monitor_words,
  AssessmentFormat.progress_monitor_words_v2,
  AssessmentFormat.progress_monitor_sounds,
  AssessmentFormat.progress_monitor_sounds_v2,
  AssessmentFormat.progress_monitor_letter_names,
  AssessmentFormat.progress_monitor_letter_names_v2,
  AssessmentFormat.grade_level_fluency,
  AssessmentFormat.grade_level_fluency_v2,
] as const satisfies readonly AssessmentFormat[];

/**
 * Assessment formats that support the in-app timed reading experience, where the coach marks
 * incorrect words on screen while the student reads and the app derives the stats.
 * Gated on the client by the `in-app-pm-gla` feature flag.
 */
export const IN_APP_ASSESSMENT_FORMATS = [
  AssessmentFormat.progress_monitor_words_v2,
  AssessmentFormat.progress_monitor_sounds_v2,
  AssessmentFormat.progress_monitor_letter_names_v2,
  AssessmentFormat.grade_level_fluency_v2,
  AssessmentFormat.fluency_check,
] as const satisfies readonly AssessmentFormat[];

export function isInAppAssessmentFormat(format: string): boolean {
  return (IN_APP_ASSESSMENT_FORMATS as readonly string[]).includes(format);
}

/** Grade Level Assessment V2 asks whether the student can retell only in grades 1–8. */
const GRADE_LEVEL_ASSESSMENT_V2_RETELL_GRADES = new Set<string>([
  PrismaEnums.StudentYear.first,
  PrismaEnums.StudentYear.second,
  PrismaEnums.StudentYear.third,
  PrismaEnums.StudentYear.fourth,
  PrismaEnums.StudentYear.fifth,
  PrismaEnums.StudentYear.sixth,
  PrismaEnums.StudentYear.seventh,
  PrismaEnums.StudentYear.eighth,
]);

export function shouldShowGradeLevelAssessmentV2CanRetell(grade: string | null | undefined): boolean {
  return grade != null && GRADE_LEVEL_ASSESSMENT_V2_RETELL_GRADES.has(grade);
}

/**
 * Fields the in-app timed reading form owns. Deliberately narrower than
 * `updateAssessmentResultOptionsSchema` so submitting a timed read cannot clobber
 * unrelated fields on the result.
 */
export const inAppAssessmentResultSubmissionSchema = z.object({
  incorrectWords: z.string().array(),
  incorrectWordsCount: z.number().int().nonnegative(),
  totalWordsRead: z.number().int().nonnegative(),
  totalSecondsSpent: z.number().int().positive(),
  finishedEarly: z.boolean(),
  assessmentFinished: z.literal(true),
  canRetell: z.boolean().optional(),
});

export type InAppAssessmentResultSubmission = z.infer<typeof inAppAssessmentResultSubmissionSchema>;

/** Reason captured when a teacher clears one completed in-app result so it can be run again. */
export const inAppAssessmentRedoSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

export type InAppAssessmentRedo = z.infer<typeof inAppAssessmentRedoSchema>;

export type ReadingWpmInput = {
  totalWordsRead: number | null;
  incorrectWordsCount: number | null;
  totalSecondsSpent: number | null;
  finishedEarly: boolean | null;
};

/**
 * Words correct per minute from timed-reading stats.
 * A full measured window is already a per-minute rate; an early finish is scaled up to a minute.
 * Returns null when words-read or mistake count is missing.
 */
export function calculateReadingWPM({
  totalWordsRead,
  incorrectWordsCount,
  totalSecondsSpent,
  finishedEarly,
}: ReadingWpmInput): number | null {
  if (totalWordsRead === null || incorrectWordsCount === null) {
    return null;
  }

  if (totalWordsRead < incorrectWordsCount) {
    return 0;
  }

  const correctWords = totalWordsRead - incorrectWordsCount;
  if (finishedEarly && totalSecondsSpent !== null && totalSecondsSpent > 0) {
    return (correctWords / totalSecondsSpent) * 60;
  }

  return correctWords;
}

export interface SortableAssessmentResultLike {
  content: {
    internalOrder: number;
    name: string;
  };
}

export function assessmentContentOrderAsc(a: SortableAssessmentResultLike, b: SortableAssessmentResultLike): number {
  const contentA = a.content;
  const contentB = b.content;

  return contentA.internalOrder === contentB.internalOrder
    ? contentA.name.localeCompare(contentB.name)
    : contentA.internalOrder < contentB.internalOrder
      ? -1
      : 1;
}

export const createAssessmentResultSchema = z.object({
  sessionId: z.string(),
  coachId: z.string(),
  studentId: z.string(),
  assessmentContentId: z.string(),
  assessmentId: z.string(),
});

export type CreateAssessmentResult = z.infer<typeof createAssessmentResultSchema>;

export const assessmentResultComparatorByContentInternalOrder = (contentsById: Map<string, AssessmentContent>) => {
  return (a: AssessmentResult, b: AssessmentResult): number => {
    const contentA = contentsById.get(a.assessmentContentId);
    const contentB = contentsById.get(b.assessmentContentId);
    if (!contentA) return 1;
    if (!contentB) return -1;

    return contentA.internalOrder - contentB.internalOrder || contentA.name.localeCompare(contentB.name);
  };
};

export const isUpperCaseLetterNameContent = (contentName: string) => contentName === 'Upper Case Letter Names';
export const isLowerCaseLetterNameContent = (contentName: string) => contentName === 'Lower Case Letter Names';
export const isConsonantSounds = (contentName: string) => contentName === 'Consonant Sounds';
export const isVowelSounds = (contentName: string) => contentName === 'Vowel Sounds';

/** Empty CSV cells parse as ""; missing columns become undefined; client upload also converts "" to null. */
const csvOptionalString = z.string().optional().nullable();

/**
 * CSV tokens for `requireStudentGrades`: `k` / `pre_k` (case-insensitive) and `1`–`13`.
 * Mirrors `studentYearInputSchemaMap` without importing `student.ts` (that module imports this file).
 */
const REQUIRE_STUDENT_GRADE_CSV_TOKEN_TO_YEAR: Record<string, PrismaEnums.StudentYear> = {
  pre_k: PrismaEnums.StudentYear.Pre_K,
  k: PrismaEnums.StudentYear.K,
  '1': PrismaEnums.StudentYear.first,
  '2': PrismaEnums.StudentYear.second,
  '3': PrismaEnums.StudentYear.third,
  '4': PrismaEnums.StudentYear.fourth,
  '5': PrismaEnums.StudentYear.fifth,
  '6': PrismaEnums.StudentYear.sixth,
  '7': PrismaEnums.StudentYear.seventh,
  '8': PrismaEnums.StudentYear.eighth,
  '9': PrismaEnums.StudentYear.ninth,
  '10': PrismaEnums.StudentYear.tenth,
  '11': PrismaEnums.StudentYear.eleventh,
  '12': PrismaEnums.StudentYear.twelfth,
  '13': PrismaEnums.StudentYear.thirteenth,
};

export function studentYearFromRequireGradesCsvToken(token: string): PrismaEnums.StudentYear | undefined {
  const trimmed = token.trim();
  if (!trimmed) {
    return undefined;
  }

  return REQUIRE_STUDENT_GRADE_CSV_TOKEN_TO_YEAR[trimmed.toLowerCase()];
}

const csvOptionalAssessmentFormat = csvOptionalString.transform((val, ctx): AssessmentFormat | undefined => {
  const trimmed = val?.trim() ?? '';
  if (trimmed === '') {
    return undefined;
  }

  const parsed = assessmentFormatSchema.safeParse(trimmed);
  if (!parsed.success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Invalid format "${trimmed}"`,
    });
    return z.NEVER;
  }

  return parsed.data;
});

const csvOptionalWordList = csvOptionalString.transform((val): string[] => {
  if (val == null || val.trim() === '') {
    return [];
  }

  return val
    .split(',')
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
});

const csvOptionalReadingPassage = csvOptionalString.transform((val): string | null => {
  const trimmed = val?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
});

const csvOptionalRequireStudentGrades = csvOptionalString.transform((val, ctx): PrismaEnums.StudentYear[] => {
  if (val == null || val.trim() === '') {
    return [];
  }

  const grades: PrismaEnums.StudentYear[] = [];
  for (const part of val.split(',')) {
    const token = part.trim();
    if (token.length === 0) {
      continue;
    }

    const grade = studentYearFromRequireGradesCsvToken(token);
    if (!grade) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid student grade "${token}". Use K, Pre_K, or 1-13.`,
      });
      return z.NEVER;
    }

    grades.push(grade);
  }

  return grades;
});

export const createAssessmentContentCsvRow = z.object({
  assessmentName: z
    .string({ required_error: 'assessmentName column is required' })
    .trim()
    .min(1, { message: 'assessmentName must not be empty' }),
  format: csvOptionalAssessmentFormat,
  contentName: z
    .string({ required_error: 'contentName column is required' })
    .trim()
    .min(1, { message: 'contentName must not be empty' }),
  wordList: csvOptionalWordList,
  readingPassage: csvOptionalReadingPassage,
  requireStudentGrades: csvOptionalRequireStudentGrades,
});
export type CreateAssessmentContentCsvRow = z.infer<typeof createAssessmentContentCsvRow>;

export const CreateAssessmentContentCsvFormKeys = {
  csvFile: 'csvFile',
} as const;

/**
 * Default AssessmentContent.format by assessment name for CSV upload. Only support
 * - PM
 * - Fluency check
 * - GLA
 */
export const AssessmentFormatByName: { readonly [assessmentName: string]: AssessmentFormat | undefined } = {
  'Progress Monitor Words': AssessmentFormat.progress_monitor_words,
  'Progress Monitor Words V2': AssessmentFormat.progress_monitor_words_v2,
  'Progress Monitor Sounds': AssessmentFormat.progress_monitor_sounds,
  'Progress Monitor Sounds V2': AssessmentFormat.progress_monitor_sounds_v2,
  'Progress Monitor Text': AssessmentFormat.progress_monitor_text,
  'Fluency Check': AssessmentFormat.fluency_check,
  'Progress Monitor Letter Names': AssessmentFormat.progress_monitor_letter_names,
  'Progress Monitor Letter Names V2': AssessmentFormat.progress_monitor_letter_names_v2,
  'Grade Level Fluency': AssessmentFormat.grade_level_fluency,
  'Grade Level Assessment V2': AssessmentFormat.grade_level_fluency_v2,
};
